import {
  Card,
  Divider,
  Grid,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Snackbar,
  Alert as MuiAlert,
  TableContainer,
  CardContent,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow,
  ShowNoRecordsFound,
  ErrorScreen
} from '../../utils/ui_utils'
import moment from 'moment'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty } from '../../utils/utils'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enLocale from 'date-fns/locale/en-IN'
import { useDispatch } from 'react-redux'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useFetchWaiters } from '../../modules/waiters/waiterHooks'
import { useFetchWaiterItemWiseReport } from '../../modules/reports/reportsHooks.ts'
import { clearWaiterItemWiseState } from '../../modules/reports/reportsSlice.ts'
import * as XLSX from 'xlsx'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'

var JSONbig = require('json-bigint')({ useNativeBigInt: true })

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const WaiterItemWiseReport = () => {
  // Common states
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)
  const [pagecount, setPageCount] = useState(1)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  // Commission mode filter: 0 = All, 1 = Item Sale, 2 = Value Sale
  const [commissionMode, setCommissionMode] = useState(0)
  const [selectedWaiter, setSelectedWaiter] = useState('')
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [toDate, setToDate] = useState(moment().startOf('day'))
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showToDateError, setShowToDateError] = useState(false)
  const [toDateErrorMessage, setToDateErrorMessage] = useState(
    'To Date is earlier than From Date'
  )
  const [goToPageInput, setGoToPageInput] = useState('')

  // Report data states
  const [itemWiseData, setItemWiseData] = useState([])
  const [itemWiseAggregate, setItemWiseAggregate] = useState(null)
  const [reportVisibility, setReportVisibility] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const history = useNavigate()
  const { outletFetch, outletFetchData, outletFetchLoading } = useFetchOutlet()
  const { fetchWaiters: fetchWaitersApi, fetchWaitersData, fetchWaitersLoading } = useFetchWaiters()
  const { waiterItemWiseReportLoading, waiterItemWiseReportData, waiterItemWiseReportFetch } = useFetchWaiterItemWiseReport()
  const dispatch = useDispatch()

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity = 'success') => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    fetchReportData(value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const pageCount = (total) => {
    let count = Math.ceil(total / limit)
    setPageCount(count)
  }

  // Fetch outlets
  const fetchOutlets = async () => {
    await outletFetch({ pageNumber: 1, limit: 200 })
  }

  // Fetch waiters for selected outlet
  const fetchWaiters = async (outletId) => {
    if (isEmpty(outletId)) return
    await fetchWaitersApi({ outlet_id: outletId, pageNumber: 1, limit: 1000 })
  }

  // Fetch report data
  const fetchReportData = async (pageNum = page) => {
    const tempFromDate = moment(fromDate)
    const tempToDate = moment(toDate)

    if (tempFromDate.isAfter(tempToDate)) {
      toastMessage('From Date cannot be after To Date', 'error')
      return
    }

    if (isEmpty(selectedWaiter)) {
      toastMessage('Please select a waiter', 'error')
      return
    }

    const fromDateStr = moment(fromDate).format('YYYY-MM-DD')
    const toDateStr = moment(toDate).add(1, 'days').format('YYYY-MM-DD')

    setHasFetched(true)
    await waiterItemWiseReportFetch({
      from_date: fromDateStr,
      to_date: toDateStr,
      waiter_id: selectedWaiter,
      outlet_id: selectedOutlet,
      pageNumber: pageNum,
      limit,
      commission_mode: commissionMode
    })
  }

  // Initialize
  useEffect(() => {
    businessCheck(history)
    fetchOutlets()
    return () => {
      dispatch(clearWaiterItemWiseState())
    }
  }, [])

  // Update selected outlet when outlet data loads
  useEffect(() => {
    if (outletFetchData?.data && outletFetchData.data.length > 0 && isEmpty(selectedOutlet)) {
      const firstOutlet = outletFetchData.data.find(
        (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
      )
      if (firstOutlet) setSelectedOutlet(firstOutlet._id)
    }
  }, [outletFetchData])

  // Update selected waiter when waiter data loads
  useEffect(() => {
    if (fetchWaitersData?.data && fetchWaitersData.data.length > 0 && isEmpty(selectedWaiter)) {
      setSelectedWaiter(fetchWaitersData.data[0]._id)
    }
  }, [fetchWaitersData])

  // Fetch waiters when outlet changes
  useEffect(() => {
    if (!isEmpty(selectedOutlet)) {
      fetchWaiters(selectedOutlet)
      setSelectedWaiter('') // Reset waiter selection
    }
  }, [selectedOutlet])

  // Update item-wise report data
  useEffect(() => {
    if (waiterItemWiseReportData) {
      if (waiterItemWiseReportData.data && waiterItemWiseReportData.data.length > 0) {
        setItemWiseData(waiterItemWiseReportData.data)
        setItemWiseAggregate(waiterItemWiseReportData.aggregate)
        pageCount(waiterItemWiseReportData.total)
        setReportVisibility(true)
      } else {
        setItemWiseData([])
        setItemWiseAggregate(null)
        setReportVisibility(false)
      }
    }
  }, [waiterItemWiseReportData])

  // Helper: convert Comm_Mode (int or string) to a readable label
  const modeLabel = (cm) => {
    const v = cm === null || cm === undefined ? null : Number(cm)
    if (v === 1) return 'Item Sale'
    if (v === 2) return 'Value Sale'
    return ''
  }

  // Helper: green/blue dot color based on Comm_Mode
  const modeDotColor = (cm) => {
    const v = cm === null || cm === undefined ? null : Number(cm)
    if (v === 1) return '#2e7d32' // green
    if (v === 2) return '#1565c0' // blue
    return null
  }

  // Export to Excel
  const exportToExcel = () => {
    let data = [...itemWiseData]
    let filename = 'Waiter_ItemWise_Report'
    const isAllMode = Number(commissionMode) === 0
    let headers = [
      { field: 'IName', title: 'Item Name' },
      { field: 'IPrice', title: 'Price' },
      { field: 'Total_Qty', title: 'Qty' },
      { field: 'Total', title: 'Amount' },
      { field: 'Comm_Per', title: 'IComm %' },
      { field: 'Comm_Amt', title: 'IComm Amt' },
      ...(isAllMode ? [{ field: 'Mode_Label', title: 'Mode' }] : [])
    ]

    // Tag each row with a Mode label (only used in 'all' mode CSV column)
    data = data.map((row) => ({ ...row, Mode_Label: modeLabel(row?.Comm_Mode) }))

    // No records — show a single 'No records found' row instead of an empty sheet
    if (data.length === 0) {
      data.push({ IName: 'No records found', __label: true })
    }

    if (itemWiseAggregate) {
      data.push(
        {
          IName: 'TOTAL',
          Total_Qty: itemWiseAggregate.Qty ?? '-',
          Total: itemWiseAggregate.Total_Amount?.toFixed(2) ?? '-',
          Comm_Amt:
            itemWiseAggregate.Commission_Amount?.toFixed(2) ?? '-'
        }
      )
    }

    try {
      const ws = XLSX.utils.json_to_sheet(
        data.map((row) => {
          const newRow = {}
          headers.forEach((h) => {
            if (row.__label) {
              newRow[h.title] = h.field === 'IName' ? (row.IName ?? '') : ''
            } else {
              newRow[h.title] = row[h.field] ?? '-'
            }
          })
          return newRow
        })
      )
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Report')
      XLSX.writeFile(wb, `${filename}_${moment().format('YYYY-MM-DD')}.xlsx`)
      toastMessage('Report exported successfully')
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      toastMessage('Error exporting report', 'error')
    }
  }


  // Refresh functionality
  const refreshFunction = () => fetchReportData(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh} = useReportRefresh(
    refreshFunction,
    [page, selectedOutlet, selectedWaiter, fromDate, toDate, commissionMode]
  )

  // Auto-fetch report when filters change
  useEffect(() => {
    if (!isEmpty(selectedWaiter) && !isEmpty(selectedOutlet)) {
      setPage(1)
      fetchReportData(1)
    }
  }, [selectedOutlet, selectedWaiter, fromDate, toDate, commissionMode])

  return (
    <Box sx={{ p: 2 }}>
      <Helmet title="Waiter Item-Wise Report" />

      {/* Header */}
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Waiter Item-Wise Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={waiterItemWiseReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mb: 3 }} />

      {/* Filters */}
      <MainCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Outlet Selector */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Outlet</InputLabel>
                <Select
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                  label="Outlet"
                  disabled={outletFetchLoading}
                >
                  {(outletFetchData?.data || [])
                    .filter((o) => String(o?._id) !== '0' && o?.name !== 'All Outlets')
                    .map((outlet) => (
                    <MenuItem key={outlet._id} value={outlet._id}>
                      {outlet.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Commission Mode Selector */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Commission Mode</InputLabel>
                <Select
                  value={commissionMode}
                  onChange={(e) => setCommissionMode(e.target.value)}
                  label="Commission Mode"
                >
                  <MenuItem value={0}>All</MenuItem>
                  <MenuItem value={1}>Item Sale</MenuItem>
                  <MenuItem value={2}>Value Sale</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Waiter Selector */}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Waiter</InputLabel>
                <Select
                  value={selectedWaiter}
                  onChange={(e) => setSelectedWaiter(e.target.value)}
                  label="Waiter"
                  disabled={fetchWaitersLoading || (fetchWaitersData?.data?.length || 0) === 0}
                >
                  {(fetchWaitersData?.data || []).map((waiter) => (
                    <MenuItem key={waiter._id} value={waiter._id}>
                      {waiter.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            {/* From Date */}
            <Grid item xs={12} sm={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
                <DatePicker
                  label="From Date"
                  value={fromDate.toDate()}
                  onChange={(newValue) => {
                    setFromDate(moment(newValue))
                    if (moment(toDate).isBefore(newValue)) {
                      setShowToDateError(true)
                    } else {
                      setShowToDateError(false)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={showError}
                      helperText={showError ? errorMessage : ''}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* To Date */}
            <Grid item xs={12} sm={6} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
                <DatePicker
                  label="To Date"
                  value={toDate.toDate()}
                  onChange={(newValue) => {
                    setToDate(moment(newValue))
                    if (moment(fromDate).isAfter(newValue)) {
                      setShowToDateError(true)
                    } else {
                      setShowToDateError(false)
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={showToDateError}
                      helperText={showToDateError ? toDateErrorMessage : ''}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </CardContent>
      </MainCard>

      {/* Report Display */}
      {waiterItemWiseReportLoading && (
        <Grid container spacing={10} direction={'column'} sx={{ paddingTop: 5 }}>
          <Grid item>
            <Grid container rowSpacing={4.5} columnSpacing={10} sx={{ mt: 1 }}>
              <Grid item xs sm md={8}>
                <MainCard content={false} sx={{ mt: 1.5 }}>
                  <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                    <ListTypeShimmer />
                  </Box>
                </MainCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!waiterItemWiseReportLoading && reportVisibility && (
        <>

          {/* Export Button */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Export to Excel" placement="top" arrow>
              <IconButton variant="contained" onClick={exportToExcel} size="large">
                <CloudUploadIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mode legend (only when 'all' is selected) */}
          {Number(commissionMode) === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                  Item Sale
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1565c0' }} />
                <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600 }}>
                  Value Sale
                </Typography>
              </Box>
            </Box>
          )}

          {/* Report Table */}
          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Item Name</StyledTableCell>
                  <StyledTableCell align="right">Price</StyledTableCell>
                  <StyledTableCell align="right">Qty</StyledTableCell>
                  <StyledTableCell align="right">Amount</StyledTableCell>
                  <StyledTableCell align="right">IComm %</StyledTableCell>
                  <StyledTableCell align="right">IComm Amt</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {itemWiseAggregate && (
                  <TableRow sx={{ backgroundColor: '#ECF9FF' }}>
                    <StyledTableCell sx={{ fontWeight: 'bold' }}>TOTAL:</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      -
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {itemWiseAggregate.Qty || 0}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {itemWiseAggregate.Total_Amount?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      -
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 'bold' }} align="right">
                      {itemWiseAggregate.Commission_Amount?.toFixed(2) || '0.00'}
                    </StyledTableCell>
                  </TableRow>
                )}
                {itemWiseData.map((row, i) => {
                  const dotColor = modeDotColor(row?.Comm_Mode)
                  return (
                  <StyledTableRow key={i}>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {dotColor && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
                        )}
                        <span>{row.IName}</span>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="right">{row.IPrice?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.Total_Qty?.toFixed(3)}</StyledTableCell>
                    <StyledTableCell align="right">{row.Total?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.Comm_Per?.toFixed(2)}</StyledTableCell>
                    <StyledTableCell align="right">{row.Comm_Amt?.toFixed(2)}</StyledTableCell>
                  </StyledTableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagecount > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                mt: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Pagination
                  count={pagecount}
                  page={page}
                  color="secondary"
                  size="small"
                  boundaryCount={2}
                  siblingCount={0}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: 'white',
                  p: 2,
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Jump to
                </Typography>
                <TextField
                  size="small"
                  value={goToPageInput}
                  onChange={handleGoToPageChange}
                  onKeyPress={handleGoToPageKeyPress}
                  placeholder="Page"
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease-in-out',
                      '& fieldset': {
                        borderColor: 'grey.300',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '1.5px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '2px',
                        boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.1)'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      textAlign: 'center',
                      fontWeight: 500,
                      padding: '7px 8px',
                      height: '20px',
                      lineHeight: '20px'
                    }
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                  of {pagecount}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={handleGoToPageSubmit}
                  disabled={
                    !goToPageInput ||
                    parseInt(goToPageInput, 10) < 1 ||
                    parseInt(goToPageInput, 10) > pagecount
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 0.75,
                    height: '34px',
                    lineHeight: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Go
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {!waiterItemWiseReportLoading && hasFetched && !reportVisibility && <ShowNoRecordsFound />}

      {/* Snackbar */}
      <Snackbar
        open={snackbar}
        autoHideDuration={SnackBarHideDuration}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleSnackbarClose}
        TransitionComponent={SnackBarTransition}
      >
        <Alert onClose={handleSnackbarClose} severity={severity}>
          {PopUpMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default WaiterItemWiseReport
